<template>
  <main class="main">
    <div class="anim-wrap" :class="{ loading: loading }">
      <div class="container" v-if="transformedScores">
        <div class="row page-nav">
          <div class="col-md-8 hidden-sm">
            <nav class="breadcrumbs">
              <ol class="breadcrumb">
                <li>
                  <a href="#" @click="$router.go(-1)" class="link-item">Hub</a>
                </li>
                <li>
                  <span class="link-item"
                    >{{ testData.firstName }} {{ testData.lastName }}</span
                  >
                </li>
              </ol>
            </nav>
          </div>
          <div class="col-md-4 text-md-right">
            <a href="#" @click="$router.go(-1)" class="link-item"
              ><font-awesome-icon icon="angle-left" /> Back</a
            >
          </div>
        </div>
        <div class="row">
          <div class="col-md-5">
            <div class="sheet-panel content-panel">
              <div class="sheet-header">
                <h3>{{ testData.firstName }} {{ testData.lastName }}</h3>
                <button
                  class="info-tip"
                  title="Below is your RIASEC score split across 6 interest areas"
                  v-tippy="{ theme: 'navy' }"
                >
                  <font-awesome-icon icon="info" />
                </button>
              </div>
              <div class="sheet-content">
                <div class="riasec-chart">
                  <div
                    v-for="item in transformedScores.scores"
                    v-bind:key="item.area"
                    class="band"
                    :class="slugifyString(item.area)"
                    v-tippy="{ theme: 'navy' }"
                    :title="item.area"
                  >
                    <div class="bar">
                      <div
                        class="fill"
                        v-bind:style="{
                          height:
                            (item.score / transformedScores.maxValue) * 100 +
                            '%',
                        }"
                      >
                        <svg>
                          <rect width="100%" height="100%" />
                        </svg>
                      </div>
                    </div>
                    <span class="letter">{{ truncateString(item.area) }}</span>
                  </div>
                </div>
                <div style="padding: 15px">
                  <h5>RIASEC distribution</h5>
                  <p>
                    Holland Occupational Themes (RIASEC) is a vocational theory
                    based on individual interests organized as Realistic
                    (Doers), Investigative (Thinkers), Artistic (Creators),
                    Social (Helpers), Enterprising (Persuaders), and
                    Conventional (Organizers).
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-7">
            <div class="sheet-panel content-panel" v-if="transformedScores">
              <div class="sheet-header">
                <h3>Interest code</h3>
                <button
                  class="info-tip"
                  title="Below is your top RIASEC interest areas"
                  v-tippy="{ theme: 'navy' }"
                >
                  <font-awesome-icon icon="info" />
                </button>
              </div>
              <div class="sheet-content">
                <div
                  v-for="item in orderedScores"
                  v-bind:key="item.area"
                  class="riasec-highlight"
                  :class="slugifyString(item.area)"
                >
                  <div class="band">
                    <div class="hexa">
                      <svg
                        width="53"
                        height="49"
                        viewBox="0 0 53 49"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M51.4019 20C53.0096 22.7846 53.0096 26.2154 51.4019 29L42.8481 43.8157C41.2404 46.6003 38.2692 48.3157 35.0538 48.3157L17.9462 48.3157C14.7308 48.3157 11.7596 46.6003 10.1519 43.8157L1.59808 29C-0.00961813 26.2154 -0.00961798 22.7846 1.59808 20L10.1519 5.1843C11.7596 2.39969 14.7308 0.6843 17.9462 0.6843L35.0538 0.684301C38.2692 0.684301 41.2404 2.39969 42.8481 5.1843L51.4019 20Z"
                        />
                      </svg>
                      <div class="letter">{{ truncateString(item.area) }}</div>
                    </div>
                  </div>
                  <div class="description">
                    <h3>{{ item.area }}</h3>
                    <p>{{ item.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row" v-if="careerMatches">
          <div class="col-md-12 py-3 mb-4">
            <h2>Interest based job matches</h2>
            <p>
              The following jobs have a good match with your RIASEC interest
              profile
            </p>
          </div>
          <div
            class="col-lg-4 col-md-6 col-sm-6"
            v-for="item in careerMatches"
            v-bind:key="item.code"
          >
            <a
              class="panel-job panel-content panel-link"
              target="_blank"
              :href="'https://traitstack.com/jobs/' + item.code + '?ref=plus'"
            >
              <div class="icon-holder">
                <img
                  :src="
                    'https://cdn.traitstack.com/static/' + item.topcat + '.svg'
                  "
                  class="svg-image"
                  alt
                />
              </div>
              <div class="title-holder">
                <h3>{{ item.title }}</h3>
                <p class="subtitle">{{ item.category }}</p>
                <button
                  class="fit"
                  :class="slugifyString(item.fit)"
                  :title="item.fit + ' match with interests'"
                  v-tippy="{ theme: 'navy' }"
                >
                  <font-awesome-icon icon="circle" />
                  <font-awesome-icon icon="circle" />
                  <font-awesome-icon class="var" icon="circle" />
                </button>
                <button
                  v-if="item.tags.bright_outlook"
                  class="bright"
                  title="This job has a bright future"
                  v-tippy="{ theme: 'navy' }"
                >
                  <font-awesome-icon icon="arrow-up" />
                </button>
              </div>
              <span class="indicator">
                <font-awesome-icon icon="external-link-alt" />
              </span>
            </a>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="sheet-panel" v-if="careerMatches">
              <div class="sheet-content text-center">
                <p>
                  <a
                    href="https://services.onetcenter.org/"
                    title="This site incorporates information from O*NET Web Services. Click to learn more."
                    target="_blank"
                    ><img
                      src="https://www.onetcenter.org/image/link/onet-in-it.svg"
                      style="width: 130px; height: 60px; border: none"
                      alt="O*NET in-it"
                  /></a>
                </p>
                <p>
                  <small
                    >This site incorporates information from
                    <a href="https://services.onetcenter.org/" target="_blank"
                      >O*NET Web Services</a
                    >
                    by the U.S. Department of Labor, Employment and Training
                    Administration (USDOL/ETA). O*NET® is a trademark of
                    USDOL/ETA.</small
                  >
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="container" v-if="loadError">
        <div class="panel panel-flat text-center">
          <h1>Error</h1>
          <p>
            The profile does not exist or you do not have permission to view the
            information.
          </p>
          <button class="btn btn-link" @click="$router.go(-1)">
            Back to previous page
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
// FireBase
import firebase from "firebase/app";
import "firebase/functions";
import { db } from "../main.js";
import { pathwaysCategories } from "@/assets/pathwaysCategories.js";

export default {
  name: "resultspathways",
  components: {},
  computed: {
    orderedScores() {
      const scores = JSON.parse(JSON.stringify(this.transformedScores.scores));
      scores.sort(function(b, a) {
        return parseFloat(a.score) - parseFloat(b.score);
      });
      return scores.slice(0, 3);
    },
    transformedCode() {
      let cat = this.activeCareer.substring(0, 2) + "-0000";
      return cat;
    },
  },
  data() {
    return {
      loadError: false,
      loading: null,
      testData: null,
      riasecScore: null,
      careerMatches: null,
      careerTotals: null,
      transformedScores: null,
      pathwaysCategories: pathwaysCategories,
    };
  },
  mounted() {
    this.id = this.$route.query.id;
    this.fetchData();
  },
  methods: {
    fetchData() {
      if (!this.id) {
        return false;
      }

      this.loading = true;
      let fetchData = firebase.functions().httpsCallable("getSessionData");

      fetchData({
        code: this.id,
      })
        .then((result) => {
          this.loading = false;
          this.loadError = false;
          this.testData = result.data;
          this.getCareerData();
        })
        .catch((error) => {
          this.loading = false;
          this.loadError = true;
          console.log(error);
        });
    },
    getCareerData() {
      let pathwaysScore = this.testData.pathways.scores;
      if (!this.testData.pathways.riasec) {
        this.axios
          .get(
            "https://services.onetcenter.org/ws/mnm/interestprofiler/results?answers=" +
              pathwaysScore +
              "&client=traitstack"
          )
          .then((response) => {
            this.transformRiasecScores(response.data.result);
          });
      } else {
        this.transformRiasecScores(this.testData.pathways.riasec);
      }

      this.axios
        .get(
          "https://services.onetcenter.org/ws/mnm/interestprofiler/careers?start=1&end=99&answers=" +
            pathwaysScore +
            "&client=traitstack"
        )
        .then((response) => {
          this.transformCareerMatches(response.data.career);
        });

      //this.tallyIndustries(this.careerMatches);
    },
    tallyIndustries(careers) {
      let totals = {};
      careers.forEach((item) => {
        if (item.topcat in totals) {
          totals[item.topcat]++;
        } else {
          totals[item.topcat] = 1;
        }
      });
      let entries = Object.entries(totals);
      let sorted = entries.sort((b, a) => a[1] - b[1]);
      console.log(sorted);
    },
    slugifyString(text) {
      text = text.toLowerCase();
      text = text.substring(0, 3);
      return text;
    },
    truncateString(text) {
      text = text.substring(0, 1);
      return text;
    },
    transformCareerMatches(careerData) {
      const careers = [];
      careerData.forEach((element) => {
        element.data = null;
        let cat = element.code.substring(0, 2);
        element.topcat = cat + "-0000";
        element.category = this.pathwaysCategories[cat + "-0000"];
        careers.push(element);
      });
      this.careerMatches = careers;
    },
    transformRiasecScores(scoreData) {
      let maxValue = 0;

      scoreData.forEach((element) => {
        if (element.score > maxValue) {
          maxValue = element.score;
        }
      });

      maxValue = maxValue + 3;

      const scores = {
        maxValue: maxValue,
        scores: scoreData,
      };

      this.transformedScores = scores;
    },
  },
};
</script>
